import {
  Action,
  combineReducers,
  configureStore,
  createListenerMiddleware,
  Reducer,
  Store,
} from "@reduxjs/toolkit";
import { ThunkDispatch } from "redux-thunk";
import visitorTrackingReducer from "../reducers/visitorTracking";

const rootReducer: Reducer = combineReducers({
  visitorTracking: visitorTrackingReducer,
});

const listenerMiddleware = createListenerMiddleware({
  onError: (error) => console.error("middlewareError", error),
});

export const startAppListening =
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>();

// Define a store type that doesn't reference node_modules paths
export type AppStore = Store<RootState> & {
  dispatch: AppDispatch;
};

const setupStore = (preloadedState?: Partial<RootState>): AppStore => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().prepend(listenerMiddleware.middleware),
    preloadedState,
  }) as AppStore;
};

export interface PreviewExtraArgument {
  isMockEnabled?: boolean;
}

export const setupPreviewStore = (
  preloadedState?: Partial<RootState>
): AppStore => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: {
            isMockEnabled: true,
          },
        },
      }).prepend(listenerMiddleware.middleware),
    preloadedState,
  }) as AppStore;
};

export type RootState = ReturnType<typeof rootReducer>;
type AppAction = Action<any>;

/**
 * We use a singleton store to avoid creating multiple stores
 * especially in web components where the store could be created multiple times
 */
let store: AppStore;

const getStore = (): AppStore => {
  if (!store) {
    store = setupStore();
  }
  return store;
};

export type AppDispatch = ThunkDispatch<RootState, any, AppAction>;
export default getStore;
